import React, { useState, useRef, useEffect, useCallback } from 'react';

const ThreeDViewer = ({ frames, initialScale = 1, onClose }) => {
    const canvasRef = useRef(null);
    const [images, setImages] = useState([]);
    const [loadedCount, setLoadedCount] = useState(0);
    const [currentFrame, setCurrentFrame] = useState(0);
    const [scale, setScale] = useState(initialScale);
    const [autoRotate, setAutoRotate] = useState(true);
    const rotationInterval = useRef(null);

    const startX = useRef(0);
    const isDragging = useRef(false);

    // Блокируем скролл body при открытии модалки
    useEffect(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = originalOverflow; };
    }, []);

    // Предзагрузка всех изображений
    useEffect(() => {
        const imgArr = [];
        let count = 0;

        frames.forEach(src => {
            const img = new Image();
            img.src = src;
            img.onload = () => {
                count++;
                setLoadedCount(count);
            };
            imgArr.push(img);
        });

        setImages(imgArr);
    }, [frames]);

    // Рисуем кадр на canvas
    const drawFrame = useCallback(
        (frameIndex) => {
            if (!canvasRef.current || images.length === 0) return;
            const ctx = canvasRef.current.getContext('2d');
            const img = images[frameIndex];
            if (!img.complete) return;

            const canvas = canvasRef.current;
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const imgWidth = img.width * scale;
            const imgHeight = img.height * scale;

            ctx.drawImage(
                img,
                (canvas.width - imgWidth) / 2,
                (canvas.height - imgHeight) / 2,
                imgWidth,
                imgHeight
            );
        },
        [images, scale]
    );

    useEffect(() => {
        if (!frames || frames.length === 0) return;
        if (loadedCount === frames.length) drawFrame(0);
    }, [loadedCount, frames, drawFrame]);

    useEffect(() => {
        drawFrame(currentFrame);
    }, [currentFrame, scale, images, drawFrame]);

    // Автопрокрутка
    useEffect(() => {
        if (autoRotate && images.length > 0) {
            rotationInterval.current = setInterval(() => {
                setCurrentFrame(prev => (prev + 1) % images.length);
            }, 90); // скорость (мс)
        }
        return () => clearInterval(rotationInterval.current);
    }, [autoRotate, images]);

    // Обработчики мыши
    const handleMouseDown = (e) => {
        stopAutoRotate();
        isDragging.current = true;
        startX.current = e.clientX;

        window.addEventListener('mousemove', handleMouseMoveGlobal);
        window.addEventListener('mouseup', handleMouseUpGlobal);
    };

    const handleMouseMoveGlobal = (e) => {
        if (!isDragging.current || images.length === 0) return;
        const delta = e.clientX - startX.current;
        if (Math.abs(delta) >= 5) {
            setCurrentFrame(prev =>
                delta > 0
                    ? (prev + 1) % images.length
                    : (prev - 1 + images.length) % images.length
            );
            startX.current = e.clientX;
        }
    };

    const handleMouseUpGlobal = () => {
        isDragging.current = false;
        window.removeEventListener('mousemove', handleMouseMoveGlobal);
        window.removeEventListener('mouseup', handleMouseUpGlobal);
    };

    // Touch
    const handleTouchStart = (e) => {
        stopAutoRotate();
        isDragging.current = true;
        startX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current || images.length === 0) return;
        const delta = e.touches[0].clientX - startX.current;
        if (Math.abs(delta) >= 1) {
            setCurrentFrame(prev =>
                delta > 0
                    ? (prev + 1) % images.length
                    : (prev - 1 + images.length) % images.length
            );
            startX.current = e.touches[0].clientX;
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
    };

    // Колёсико (зум)
    const handleWheel = (e) => {
        const newScale = scale - e.deltaY * 0.001;
        setScale(Math.min(Math.max(newScale, 1), 3)); // минимум 1 (оригинал), максимум 3
    };

    // Остановка авто-прокрутки при ручном управлении
    const stopAutoRotate = () => {
        setAutoRotate(false);
        clearInterval(rotationInterval.current);
    };

    return (
        <div className="modal-3d-overlay">
            <div
                className="modal-3d-content"
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onWheel={handleWheel}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="btn-close modal-3d-close-btn"
                    aria-label="Close"
                    onClick={onClose}
                />

                {loadedCount < frames.length && (
                    <div className="modal-3d-progress">
                        <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${Math.round((loadedCount / frames.length) * 100)}%` }}></div>
                        </div>
                        <span>Загрузка: {Math.round((loadedCount / frames.length) * 100)}%</span>
                    </div>
                )}

                {loadedCount === frames.length && (
                    <canvas
                        ref={canvasRef}
                        width={800}
                        height={600}
                        className="modal-3d-canvas"
                        style={{ cursor: isDragging.current ? 'grabbing' : 'grab' }}
                    />
                )}
            </div>
        </div>
    );
};

export default ThreeDViewer;
